/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Cart, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

/** The Lumovy allergen list on the order, which the till checks and the kitchen shows. */
export const ORDER_ALLERGENS_KEY = 'ITS_FoodPreferences';

export interface IOrderAllergen {
    Code: string;
    Label: string;
}

interface IStoredPreference {
    PreferenceType?: string | number;
    Code?: string;
    Label?: string;
}

const PREFERENCE_TYPES: { [name: string]: number } = { notdefined: 0, allergy: 1, diet: 2, intolerance: 4, preference: 8 };

const ALLERGY = 1;

const storedPreferences = (value: string): (IStoredPreference & { Code: string })[] => {
    if (!value) {
        return [];
    }

    try {
        const parsed = JSON.parse(value) as { TransactionPreferences?: IStoredPreference[] } | null;
        const preferences = parsed?.TransactionPreferences;

        return Array.isArray(preferences)
            ? preferences.filter(
                  (preference): preference is IStoredPreference & { Code: string } =>
                      typeof preference?.Code === 'string' && !!preference.Code
              )
            : [];
    } catch {
        return [];
    }
};

/** Lumovy writes the type as a number, and reads it as a number or a name. */
const preferenceType = (preference: IStoredPreference): number | undefined =>
    typeof preference.PreferenceType === 'number'
        ? preference.PreferenceType
        : PREFERENCE_TYPES[String(preference.PreferenceType ?? '').toLowerCase()];

const preferenceKey = (preference: IStoredPreference & { Code: string }): string =>
    `${preferenceType(preference) ?? ''}|${preference.Code.trim().toUpperCase()}`;

/** The order's allergen list as Lumovy stores it, or empty. */
export const readOrderAllergensValue = (cart?: Cart): string =>
    cart?.ExtensionProperties?.find(property => property.Key === ORDER_ALLERGENS_KEY)?.Value?.StringValue ?? '';

/**
 * The allergens on an order, as Lumovy records them. Its list can also hold the guest's diets and other preferences, which are
 * not allergens and are left out.
 * @param value - The stored list, `{"TransactionPreferences":[{"PreferenceType":…,"Code":…,"Label":…}]}`.
 * @returns One entry per allergen, labelled as stored; none where the value is empty or unreadable.
 */
export const parseOrderAllergens = (value: string): IOrderAllergen[] =>
    storedPreferences(value)
        .filter(preference => preferenceType(preference) === ALLERGY)
        .map(preference => ({ Code: preference.Code, Label: preference.Label || preference.Code }));

export const readOrderAllergens = (cart?: Cart): IOrderAllergen[] => parseOrderAllergens(readOrderAllergensValue(cart));

/**
 * The allergens an order update sends: only those added since the order was read. The store adds what it is sent and never
 * removes anything, so sending the whole list would put back an allergen the venue had removed in the meantime.
 * @param cart - This channel's copy of the order.
 * @param heldAllergens - The allergen list the store held when the order was last read.
 * @returns The header property to send, or none.
 */
export const orderAllergensChange = (cart: Cart, heldAllergens: string): CommerceProperty[] => {
    const heldKeys = new Set(storedPreferences(heldAllergens).map(preferenceKey));
    const added = storedPreferences(readOrderAllergensValue(cart)).filter(preference => !heldKeys.has(preferenceKey(preference)));

    return added.length > 0
        ? [{ Key: ORDER_ALLERGENS_KEY, Value: { StringValue: JSON.stringify({ TransactionPreferences: added }) } }]
        : [];
};
